import { useNostr } from "@nostrify/react";
import { useQuery } from "@tanstack/react-query";

import {
  ADMIN_PUBKEY,
  ORDER_RELAYS,
  ORDER_STATUS_KIND,
  ORDER_STATUSES,
  type OrderStatus,
} from "@/lib/cashcraft";

export interface PublicOrderStatus {
  status: OrderStatus;
  note: string;
  updatedAt: number;
}

/**
 * Public status lookup for a single order by its event id. No login required.
 *
 * Only the operator's own (authored) status events are trusted, so we filter
 * by `authors`. Returns `null` if no status has been published yet (treated as
 * "new" by consumers).
 */
export function useOrderStatus(orderId: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<PublicOrderStatus | null>({
    queryKey: ["order-status", orderId],
    enabled: !!orderId,
    refetchInterval: 30_000,
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(8000)]);
      const filter = {
        kinds: [ORDER_STATUS_KIND],
        authors: [ADMIN_PUBKEY],
        "#d": [orderId as string],
        limit: 10,
      };

      const results = await Promise.allSettled([
        nostr.group(ORDER_RELAYS).query([filter], { signal }),
        nostr.query([filter], { signal }),
      ]);

      let best: PublicOrderStatus | null = null;
      for (const r of results) {
        if (r.status !== "fulfilled") continue;
        for (const event of r.value) {
          const status = event.tags.find(([n]) => n === "status")?.[1];
          if (!status || !ORDER_STATUSES.includes(status as OrderStatus)) {
            continue;
          }
          if (!best || event.created_at > best.updatedAt) {
            best = {
              status: status as OrderStatus,
              note: event.content,
              updatedAt: event.created_at,
            };
          }
        }
      }
      return best;
    },
  });
}
